import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// =============================================
// APP STATE CONTEXT
// Global state management with localStorage persistence
// =============================================

const AppStateContext = createContext(null);

// Storage key prefix for localStorage
const STORAGE_PREFIX = 'appState_';

// Default states for each page
const DEFAULT_STATES = {
    viewProducts: {
        currentPage: 1,
        itemsPerPage: 5,
        searchQuery: '',
        categoryFilter: '',
        marketplaceFilter: '',
        selectedProductIds: [],
        expandedProduct: null,
    },

    addProduct: {
        currentStep: 1,
        processedImages: [],
        formData: {
            uploadedImages: [],
            uploadedImagesBase64: [],
            processedImagesBase64: [],
            title: '',
            description: '',
            sku: '',
            price: '',
            quantity: '1',
            brand: '',
            color: '',
            category: '',
            condition: '',
            material: '',
            tags: '',
            metafields: {},
            variants: [{ id: 1, size: '', color: '', sku: '' }],
            selectedMarketplaces: [],
            backgroundRemoval: false,
            blurDetection: false,
            upscaleImage: false,
        },
    },

    ebaySettings: {
        selected: {
            fulfillment: '',
            payment: '',
            return: '',
            location: '',
            program: '',
        },
    },

    generalSettings: {
        settings: {
            currency: '',
            skuFormat: '',
            descriptionStyle: 'text_formatted_desc',
            descriptionPrompt: '',
            titleStyle: 'text_formatted_title',
            titlePrompt: '',
        },
        vendors: [],
        newVendor: '',
    },

    inviteUsers: {
        email: '',
        role: '',
    },
};

// Helper: Get state from localStorage
const getStoredState = (pageName) => {
    try {
        const stored = localStorage.getItem(`${STORAGE_PREFIX}${pageName}`);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error(`Failed to load state for ${pageName}:`, error);
    }
    return null;
};

// Helper: Save state to localStorage
const saveStateToStorage = (pageName, state) => {
    try {
        localStorage.setItem(`${STORAGE_PREFIX}${pageName}`, JSON.stringify(state));
    } catch (error) {
        console.error(`Failed to save state for ${pageName}:`, error);
    }
};

// Helper: Clear state from localStorage
const clearStoredState = (pageName) => {
    try {
        localStorage.removeItem(`${STORAGE_PREFIX}${pageName}`);
    } catch (error) {
        console.error(`Failed to clear state for ${pageName}:`, error);
    }
};

// =============================================
// APP STATE PROVIDER
// =============================================
export const AppStateProvider = ({ children }) => {
    // Store all page states in a single object
    const [pageStates, setPageStates] = useState({});

    // Load all stored states on mount
    useEffect(() => {
        const loadedStates = {};
        Object.keys(DEFAULT_STATES).forEach((pageName) => {
            const stored = getStoredState(pageName);
            if (stored) {
                loadedStates[pageName] = { ...DEFAULT_STATES[pageName], ...stored };
            }
        });
        if (Object.keys(loadedStates).length > 0) {
            setPageStates(loadedStates);
        }
    }, []);

    // Get state for a specific page
    const getPageState = useCallback((pageName) => {
        return pageStates[pageName] || DEFAULT_STATES[pageName] || {};
    }, [pageStates]);

    // Set state for a specific page (merges with existing)
    const setPageState = useCallback((pageName, newState) => {
        setPageStates((prev) => {
            const currentState = prev[pageName] || DEFAULT_STATES[pageName] || {};
            const mergedState = { ...currentState, ...newState };

            // Persist to localStorage
            saveStateToStorage(pageName, mergedState);

            return {
                ...prev,
                [pageName]: mergedState,
            };
        });
    }, []);

    // Clear state for a specific page (reset to defaults)
    const clearPageState = useCallback((pageName) => {
        setPageStates((prev) => {
            // Clear from localStorage
            clearStoredState(pageName);

            // Reset to defaults
            const defaults = DEFAULT_STATES[pageName] || {};
            return {
                ...prev,
                [pageName]: defaults,
            };
        });
    }, []);

    // Clear all page states (for logout)
    const clearAllPageStates = useCallback(() => {
        Object.keys(DEFAULT_STATES).forEach((pageName) => {
            clearStoredState(pageName);
        });
        // Reset all to defaults
        setPageStates({});
    }, []);

    const value = {
        getPageState,
        setPageState,
        clearPageState,
        clearAllPageStates,
    };

    return (
        <AppStateContext.Provider value={value}>
            {children}
        </AppStateContext.Provider>
    );
};

// =============================================
// CUSTOM HOOK: usePageState
// Usage: const { state, setState, clearState } = usePageState('viewProducts');
// =============================================
export const usePageState = (pageName) => {
    const context = useContext(AppStateContext);

    if (!context) {
        throw new Error('usePageState must be used within an AppStateProvider');
    }

    const { getPageState, setPageState, clearPageState } = context;

    // Get current state for this page
    const state = getPageState(pageName);

    // Setter that merges partial state
    const setState = useCallback((partialState) => {
        setPageState(pageName, partialState);
    }, [pageName, setPageState]);

    // Clear state and reset to defaults
    const clearState = useCallback(() => {
        clearPageState(pageName);
    }, [pageName, clearPageState]);

    return { state, setState, clearState };
};

export default AppStateContext;
